import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const FORM_URL = 'https://readdy.ai/api/form/d6vch0vb245v7h34om2g';

export default function FinalCTA() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      const body = new URLSearchParams({ email });
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="bg-stone-900 py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Shop CTA */}
          <div>
            <span className="inline-block text-amber-400 text-xs font-bold tracking-widest uppercase mb-4">
              Ready to Fix Your Blinds?
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight mb-5">
              Find Your Part.<br />
              <span className="text-amber-400">Order Today. Fix Tomorrow.</span>
            </h2>
            <p className="text-stone-400 text-base leading-relaxed mb-8 max-w-lg">
              Over 1,000 blind parts and window hardware components available now. Fast shipping nationwide. Our parts specialists are ready to help you find exactly what you need.
            </p>
            <div className="flex flex-wrap gap-4 mb-8">
              <button
                onClick={() => navigate('/category/vertical-blind-parts')}
                className="bg-amber-600 hover:bg-amber-500 text-white font-bold text-sm px-8 py-4 rounded-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2.5"
              >
                <i className="ri-shopping-cart-2-line text-base" />
                Shop All Parts Now
              </button>
              <a
                href="tel:+18005551234"
                className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold text-sm px-8 py-4 rounded-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2.5"
              >
                <i className="ri-phone-line text-base" />
                Call 1-800-555-1234
              </a>
            </div>
            {/* Trust signals */}
            <div className="flex flex-wrap gap-5">
              {[
                { icon: 'ri-truck-line', text: 'Free Shipping $49+' },
                { icon: 'ri-shield-check-line', text: '30-Day Returns' },
                { icon: 'ri-time-line', text: 'Ships in 1–2 Days' },
              ].map((t) => (
                <div key={t.text} className="flex items-center gap-2 text-stone-400 text-sm">
                  <i className={`${t.icon} text-amber-500`} />
                  <span>{t.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Email capture form */}
          <div className="bg-stone-800 rounded-lg border border-stone-700 p-8">
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 bg-amber-600/20 text-amber-400 text-xs font-bold px-3 py-1.5 rounded-sm mb-4 tracking-wider uppercase">
                <i className="ri-mail-line" />
                Get 10% Off Your First Order
              </div>
              <h3 className="text-white font-bold text-xl mb-2">Join Our Parts Insiders List</h3>
              <p className="text-stone-400 text-sm leading-relaxed">
                Subscribe for exclusive discounts, restocking alerts, and repair tips. Get 10% off your first order when you sign up.
              </p>
            </div>

            {status === 'success' ? (
              <div className="bg-green-900/40 border border-green-700 rounded-lg px-5 py-5 text-center">
                <i className="ri-check-circle-line text-green-400 text-3xl mb-2" />
                <p className="text-green-300 font-semibold text-sm">You&apos;re in! Check your email for your 10% discount code.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                <div>
                  <label className="text-stone-300 text-xs font-semibold uppercase tracking-wider block mb-1.5">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full bg-stone-900 border border-stone-600 focus:border-amber-500 text-white text-sm px-4 py-3 rounded-sm outline-none placeholder-stone-500 transition-colors"
                  />
                </div>
                {status === 'error' && (
                  <p className="text-red-400 text-xs">Something went wrong. Please try again.</p>
                )}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full bg-amber-600 hover:bg-amber-500 disabled:bg-stone-600 text-white font-bold text-sm py-4 rounded-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap"
                >
                  {status === 'loading' ? 'Subscribing...' : 'Get My 10% Off Code'}
                </button>
                <p className="text-stone-500 text-[11px] text-center">
                  No spam, ever. Unsubscribe anytime.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
